import { useEffect, useState, type JSX } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

/**
 * Plan 10-02 D-95 — DeactivateMachineTypeConfirmDialog
 *
 * Two-state UX (mirrors the Phase 9 DeactivateSiteConfirmDialog two-state):
 *
 *  - **Default state:** simple confirmation. The single Confirm button fires
 *    `onConfirm(false)`.
 *  - **Blocked state** (entered when the parent passes
 *    `blocked: { affectedMachineCount: number }`): surfaces the count + an
 *    explicit override checkbox. The Confirm button reads "Force Deactivate"
 *    and is disabled until the override is checked. On confirm fires
 *    `onConfirm(true)`.
 *
 * Phase 10 specific note: the Machine table does not exist yet, so the live
 * default DELETE handler always returns 204. The blocked branch exists for
 * Phase 11 — the override factory `mtDeactivateBlockedRequiresForce()` lets
 * us exercise the UX from tests today.
 */
export interface DeactivateMachineTypeConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly typeName: string;
  readonly blocked: { readonly affectedMachineCount: number } | null;
  readonly onConfirm: (force: boolean) => void;
  readonly onCancel: () => void;
  readonly submitting?: boolean;
  readonly errorMessage?: string | null;
}

export function DeactivateMachineTypeConfirmDialog({
  open,
  onOpenChange,
  typeName,
  blocked,
  onConfirm,
  onCancel,
  submitting = false,
  errorMessage,
}: DeactivateMachineTypeConfirmDialogProps): JSX.Element {
  const [overrideChecked, setOverrideChecked] = useState<boolean>(false);

  // Reset the override toggle whenever the dialog re-opens or transitions
  // between default <-> blocked.
  useEffect(() => {
    if (!open) setOverrideChecked(false);
  }, [open]);

  const isBlocked = blocked !== null;
  const confirmDisabled = submitting || (isBlocked && !overrideChecked);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="deactivate-mt-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Deactivate {typeName}?</AlertDialogTitle>
          <AlertDialogDescription>
            {isBlocked
              ? 'Existing machines reference this type. Deactivating will block creation of new machines with this type but retain references on existing ones.'
              : 'Existing machines retain their type reference, but new machines cannot be created with this type. You can reactivate it later.'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {blocked !== null && (
          <Alert variant="destructive" data-testid="deactivate-mt-blocked-alert">
            <AlertTitle>Override required</AlertTitle>
            <AlertDescription>
              <div data-testid="deactivate-mt-affected-count">
                <strong>{String(blocked.affectedMachineCount)}</strong> active machines use this
                type
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Checkbox
                  id="deactivate-mt-override"
                  checked={overrideChecked}
                  onCheckedChange={(v) => {
                    setOverrideChecked(v === true);
                  }}
                  data-testid="deactivate-mt-override"
                />
                <Label htmlFor="deactivate-mt-override" className="text-sm">
                  I understand and want to deactivate anyway
                </Label>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {errorMessage != null && errorMessage !== '' && (
          <Alert variant="destructive" data-testid="deactivate-mt-error">
            <AlertTitle>Could not deactivate</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <AlertDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onCancel();
            }}
            disabled={submitting}
            data-testid="deactivate-mt-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              onConfirm(isBlocked);
            }}
            disabled={confirmDisabled}
            data-testid="deactivate-mt-confirm"
          >
            {submitting ? 'Deactivating…' : isBlocked ? 'Force Deactivate' : 'Deactivate'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
